import type {
  SponsoredDisplayAudienceTarget,
  SponsoredDisplayCapabilities,
  SponsoredDisplayContextualTarget,
  SponsoredDisplayOptimizationGoal,
} from "@adpilot/shared";
import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { sponsoredDisplayDraftKey } from "@/lib/campaign-creation-session";
import {
  type AudienceTab,
  type CreativeDraft,
  restoreDraft,
  type SponsoredDisplayDraft,
  type UpdateDraftField,
} from "./sponsored-display-draft";

function targetIdentity(target: SponsoredDisplayAudienceTarget | SponsoredDisplayContextualTarget) {
  return JSON.stringify({ ...target, bid: null });
}

function bidForNewTarget(current: SponsoredDisplayDraft): number | null {
  if (current.bidMode !== "custom") return null;
  const parsed = Number(current.customBid);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export interface SponsoredDisplayDraftState {
  draft: SponsoredDisplayDraft;
  setDraft: Dispatch<SetStateAction<SponsoredDisplayDraft>>;
  update: UpdateDraftField;
  updateCreative: (patch: Partial<CreativeDraft>) => void;
  addAudienceTarget: (label: string, target: SponsoredDisplayAudienceTarget) => void;
  addContextualTarget: (label: string, target: SponsoredDisplayContextualTarget) => void;
  applyBidToActiveTargets: (value: number | null) => void;
}

/**
 * Owns the SD draft: restores it from session storage, persists every change and
 * reports dirtiness, and steers the draft away from modes the capabilities disallow.
 */
export function useSponsoredDisplayDraft({
  shopId,
  sponsoredDisplayCapabilities,
  onDirtyChange,
}: {
  shopId: string;
  sponsoredDisplayCapabilities: SponsoredDisplayCapabilities;
  onDirtyChange: (dirty: boolean) => void;
}): SponsoredDisplayDraftState {
  const { t } = useTranslation("campaignCreation");
  const storageKey = sponsoredDisplayDraftKey(shopId);
  const [draft, setDraft] = useState(() =>
    restoreDraft(storageKey, t("sd.campaign.defaultNamePrefix"), t("sd.adGroup.defaultNamePrefix")),
  );
  const initialFingerprint = useRef(JSON.stringify(draft));

  useEffect(() => {
    const fingerprint = JSON.stringify(draft);
    onDirtyChange(fingerprint !== initialFingerprint.current);
    try {
      sessionStorage.setItem(storageKey, fingerprint);
    } catch {
      // Storage can be denied in privacy modes; the form remains fully usable in memory.
    }
  }, [draft, onDirtyChange, storageKey]);

  useEffect(() => {
    const audienceSupported = [
      sponsoredDisplayCapabilities.targeting.amazonAudience,
      sponsoredDisplayCapabilities.targeting.viewsRemarketing,
      sponsoredDisplayCapabilities.targeting.purchasesRemarketing,
    ].some((capability) => capability.state === "supported");
    if (draft.targetingMode === "audience" && !audienceSupported) {
      setDraft((current) => ({ ...current, targetingMode: "contextual" }));
    } else if (
      draft.targetingMode === "contextual" &&
      sponsoredDisplayCapabilities.targeting.contextual.state !== "supported" &&
      audienceSupported
    ) {
      setDraft((current) => ({ ...current, targetingMode: "audience" }));
    }
  }, [draft.targetingMode, sponsoredDisplayCapabilities]);

  useEffect(() => {
    const supportedGoals: SponsoredDisplayOptimizationGoal[] = [
      ...(sponsoredDisplayCapabilities.optimization.pageVisits.state === "supported"
        ? (["page_visits"] as const)
        : []),
      ...(sponsoredDisplayCapabilities.optimization.conversions.state === "supported"
        ? (["conversions"] as const)
        : []),
      ...(sponsoredDisplayCapabilities.optimization.viewableImpressions.state === "supported"
        ? (["viewable_impressions"] as const)
        : []),
    ];
    const supportedAudienceTabs: AudienceTab[] = [
      ...(sponsoredDisplayCapabilities.targeting.amazonAudience.state === "supported"
        ? (["amazon"] as const)
        : []),
      ...(sponsoredDisplayCapabilities.targeting.viewsRemarketing.state === "supported"
        ? (["views"] as const)
        : []),
      ...(sponsoredDisplayCapabilities.targeting.purchasesRemarketing.state === "supported"
        ? (["purchases"] as const)
        : []),
    ];
    const nextGoal = supportedGoals.includes(draft.optimizationGoal)
      ? draft.optimizationGoal
      : supportedGoals[0];
    const nextAudienceTab = supportedAudienceTabs.includes(draft.audienceTab)
      ? draft.audienceTab
      : supportedAudienceTabs[0];
    const goalCorrection = nextGoal && nextGoal !== draft.optimizationGoal;
    const audienceTabCorrection = nextAudienceTab && nextAudienceTab !== draft.audienceTab;
    if (goalCorrection || audienceTabCorrection) {
      setDraft((current) => ({
        ...current,
        ...(goalCorrection ? { optimizationGoal: nextGoal } : {}),
        ...(audienceTabCorrection ? { audienceTab: nextAudienceTab } : {}),
      }));
    }
  }, [draft.audienceTab, draft.optimizationGoal, sponsoredDisplayCapabilities]);

  function update<K extends keyof SponsoredDisplayDraft>(key: K, value: SponsoredDisplayDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function updateCreative(patch: Partial<CreativeDraft>) {
    setDraft((current) => ({ ...current, creative: { ...current.creative, ...patch } }));
  }

  function addAudienceTarget(label: string, target: SponsoredDisplayAudienceTarget) {
    setDraft((current) => {
      if (
        current.audienceTargets.some((row) => targetIdentity(row.target) === targetIdentity(target))
      )
        return current;
      return {
        ...current,
        audienceTargets: [
          ...current.audienceTargets,
          {
            id: crypto.randomUUID(),
            label,
            target: { ...target, bid: bidForNewTarget(current) },
          },
        ],
      };
    });
  }

  function addContextualTarget(label: string, target: SponsoredDisplayContextualTarget) {
    setDraft((current) => {
      if (
        current.contextualTargets.some(
          (row) => targetIdentity(row.target) === targetIdentity(target),
        )
      )
        return current;
      return {
        ...current,
        contextualTargets: [
          ...current.contextualTargets,
          {
            id: crypto.randomUUID(),
            label,
            target: { ...target, bid: bidForNewTarget(current) },
          },
        ],
      };
    });
  }

  function applyBidToActiveTargets(value: number | null) {
    const key = draft.targetingMode === "audience" ? "audienceTargets" : "contextualTargets";
    setDraft((current) => ({
      ...current,
      [key]: current[key].map((row) => ({ ...row, target: { ...row.target, bid: value } })),
    }));
  }

  return {
    draft,
    setDraft,
    update,
    updateCreative,
    addAudienceTarget,
    addContextualTarget,
    applyBidToActiveTargets,
  };
}
